import React, { useEffect, useRef, useState } from "react";
import { FileCheck, Upload, X, CheckCircle2, ChevronDown, ChevronRight, File as FileIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";

export default function DocChecklist({ propertyId, onChange }) {
  const [state, setState] = useState({ items: [], completed: 0, total: 0, percent: 0 });
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({}); // per-slot upload flag
  const fileRefs = useRef({});

  const load = async () => {
    const { data } = await api.get(`/properties/${propertyId}/documentation`);
    setState(data); onChange?.(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  const uploadMany = async (name, fileList) => {
    if (!fileList || fileList.length === 0) return;
    const item = state.items.find((i) => i.name === name);
    const remaining = (item?.max_files || 4) - (item?.count || 0);
    if (remaining <= 0) {
      toast.error(`Limite di ${item?.max_files || 4} file già raggiunto per "${name}"`);
      return;
    }
    const files = Array.from(fileList).slice(0, remaining);
    if (files.length < fileList.length) {
      toast.info(`Caricherò solo ${files.length} file (limite ${item?.max_files || 4} raggiunto)`);
    }
    setBusy((b) => ({ ...b, [name]: true }));
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/properties/${propertyId}/documentation/${encodeURIComponent(name)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file caricat${files.length === 1 ? "o" : "i"} in "${name}"`);
      setExpanded((e) => ({ ...e, [name]: true }));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy((b) => ({ ...b, [name]: false })); }
  };

  const removeOne = async (name, fileId) => {
    try {
      await api.delete(`/properties/${propertyId}/documentation/${encodeURIComponent(name)}`, { params: { file_id: fileId } });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearSlot = async (name) => {
    if (!window.confirm(`Rimuovere TUTTI i file da "${name}"?`)) return;
    try {
      await api.delete(`/properties/${propertyId}/documentation/${encodeURIComponent(name)}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const barColor = state.percent >= 80 ? "bg-emerald-500" : state.percent >= 50 ? "bg-amber-500" : "bg-rose-500";
  const badgeColor = state.percent >= 80 ? "text-emerald-600" : state.percent >= 50 ? "text-amber-600" : "text-rose-600";

  return (
    <Card className="border-border/60 p-6" data-testid="doc-checklist">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileCheck className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-medium">Motore documentale</h3>
        </div>
        <div className={`font-heading text-3xl font-semibold tabular-nums ${badgeColor}`} data-testid="doc-percent">{state.percent}%</div>
      </div>
      <Progress value={state.percent} className="h-2 mb-1" />
      <p className="text-xs text-muted-foreground">{state.completed}/{state.total} voci · fino a 4 file per voce</p>

      <div className="mt-5 space-y-2">
        {state.items.map((it) => {
          const isOpen = !!expanded[it.name];
          const count = it.count || 0;
          const canAdd = count < (it.max_files || 4);
          return (
            <div key={it.name} className="rounded-md border border-border/60 overflow-hidden" data-testid={`doc-${it.name}`}>
              <div className="flex items-center gap-3 p-2.5">
                <button
                  onClick={() => setExpanded((e) => ({ ...e, [it.name]: !isOpen }))}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label={isOpen ? "Chiudi" : "Apri"}
                >
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {it.uploaded ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <span className="w-4 h-4 rounded-full border border-muted-foreground/40 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    {it.name}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${count > 0 ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"} tabular-nums`}>
                      {count}/{it.max_files || 4}
                    </span>
                  </div>
                </div>
                <input
                  ref={(el) => (fileRefs.current[it.name] = el)}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => { uploadMany(it.name, e.target.files); e.target.value = ""; }}
                />
                {canAdd && (
                  <Button variant="outline" size="sm" onClick={() => fileRefs.current[it.name]?.click()} disabled={busy[it.name]} className="h-7 text-xs gap-1" data-testid={`upload-doc-${it.name}`}>
                    <Upload className="w-3 h-3" /> {busy[it.name] ? "…" : (count === 0 ? "Carica" : "+ Aggiungi")}
                  </Button>
                )}
                {count > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => clearSlot(it.name)} className="h-7 text-xs text-muted-foreground hover:text-destructive" data-testid={`remove-doc-${it.name}`}>Svuota</Button>
                )}
              </div>
              {isOpen && count > 0 && (
                <div className="border-t border-border/60 bg-muted/30 p-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(it.files || []).map((f) => (
                    <div key={f.file_id} className="group relative bg-background border border-border/60 rounded-md overflow-hidden" data-testid={`doc-file-${f.file_id}`}>
                      {f.preview_path ? (
                        <img src={`${API}/files/${f.preview_path}`} alt="preview" className="w-full h-24 object-cover" />
                      ) : (
                        <div className="w-full h-24 flex items-center justify-center bg-muted"><FileIcon className="w-8 h-8 text-muted-foreground/50" /></div>
                      )}
                      <div className="p-1.5">
                        <p className="text-[10px] truncate" title={f.original_filename}>{f.original_filename}</p>
                      </div>
                      <button
                        onClick={() => removeOne(it.name, f.file_id)}
                        className="absolute top-1 right-1 w-6 h-6 bg-background/90 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground transition"
                        title="Rimuovi file"
                        data-testid={`remove-file-${f.file_id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
